import styled, { css } from "styled-components";
import Input from "../../common/components/Input";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import ImgageUploader from "../../common/components/ImageUploader";
import { useAuth } from "../../hooks/useAuth";
import { IsAlbum, IsPlatform, IsWriteAlbum } from "../../type/album";
import { useImage } from "../../hooks/database/useImage";
import { DocumentData } from "firebase/firestore";
import { useBoard } from "../../hooks/database/useBoard";
import InputWithModal from "../../common/components/InputWithModal";
import StateButton from "../../common/components/StateButton";

interface IsWriteForm {
  currentDoc?: IsAlbum | DocumentData;
  vocalList: string[];
}

export default function WriteForm({ currentDoc, vocalList }: IsWriteForm) {
  const [input, setInput] = useState<IsWriteAlbum>({
    id: "",
    image: null,
    platform: "",
    title: "",
    url: "",
    currentTitle: "",
    currentVocal: "",
    currentUrl: "",
    content: [],
    vocalList: [],
    isVisible: false,
  });
  const [isAddButtonOn, setIsAddButtonOn] = useState(false);
  const [isEnterButtonOn, setIsEnterButtonOn] = useState(false);
  const [isUpload, setIsUpload] = useState(false);

  //  앞단어 적용 안됨
  let youtubeRegex = /https:\/\/music.youtube.com\/watch\?/gm;
  let appleRegex = /https:\/\/music\.apple\.com\/kr\/album\//gm;
  let melonRegex = /http:\/\/kko\.to\//gm;
  // https://music.apple.com/kr/album/soak-up-the-sun-vocals-up/1442553426?i=1442553943
  const [errorMsg, setErrorMsg] = useState("");
  const { upload, deleteImage } = useImage();
  const { user } = useAuth();
  const { addDocument } = useBoard();

  useEffect(() => {
    // if (currentDoc?.uid !== user.uid) {
    //   alert("허용되지 않은 앨범입니다");
    //   nav("/");
    // }
    if (currentDoc) {
      setInput((prev) => ({
        ...prev,
        id: currentDoc.id,
        platform: currentDoc.platform,
        title: currentDoc.title,
        url: currentDoc.url,
        content: currentDoc.content,
        isVisible: currentDoc.isVisible,
      }));
      setIsEnterButtonOn(() => true);
    }
  }, [currentDoc]);

  useEffect(() => {
    const urlArray = input.content.map(({ url, ...rest }) => url);

    if (
      input.currentUrl === "" ||
      input.currentTitle === "" ||
      input.currentVocal === ""
    ) {
      setIsAddButtonOn(false);
      setErrorMsg("");
    } else if (
      !youtubeRegex.test(input.currentUrl) &&
      !appleRegex.test(input.currentUrl) &&
      !melonRegex.test(input.currentUrl)
    ) {
      setIsAddButtonOn(false);
      setErrorMsg("올바르지 않은 Url입니다");
    } else if (urlArray.indexOf(input.currentUrl) !== -1) {
      setIsAddButtonOn(false);
      setErrorMsg("동일한 음악이 포함되어 있습니다");
    } else if (input.currentTitle && input.currentUrl && input.currentVocal) {
      setIsAddButtonOn(true);
      setErrorMsg("");
    } else {
      setErrorMsg("");
    }
  }, [input]);

  useEffect(() => {
    if (
      input.title &&
      input.content.length > 0 &&
      input.platform &&
      (input.url || input.image)
    ) {
      setIsEnterButtonOn(() => true);
    } else {
      setIsEnterButtonOn(() => false);
    }
  }, [input]);

  const setImageFile = (file: File | null) => {
    setInput((prev) => ({
      ...prev,
      image: file,
    }));
  };

  const onChangeInput = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      await setInput((prev) => {
        return { ...prev, [name]: value };
      });
    },
    []
  );

  const onSelectVocal = useCallback((vocal: string) => {
    setInput((prev) => {
      return { ...prev, currentVocal: vocal };
    });
  }, []);

  const addContent = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (!isAddButtonOn) return;

      if (input.platform !== "") {
        if (
          !(
            youtubeRegex.test(input.currentUrl) && input.platform === "youtube"
          ) &&
          !(appleRegex.test(input.currentUrl) && input.platform === "apple") &&
          !(melonRegex.test(input.currentUrl) && input.platform === "melon")
        ) {
          console.log(
            !(
              youtubeRegex.test(input.currentUrl) &&
              input.platform === "youtube"
            ) &&
              !(
                appleRegex.test(input.currentUrl) && input.platform === "apple"
              ) &&
              !(melonRegex.test(input.currentUrl) && input.platform === "melon")
          );
          setErrorMsg("같은 플랫폼의 음악만 추가할 수 있습니다");
          return;
        }
      } else if (input.platform === "") {
        if (youtubeRegex.test(input.currentUrl)) {
          setPlatform("youtube");
        } else if (appleRegex.test(input.currentUrl)) {
          setPlatform("apple");
        } else if (melonRegex.test(input.currentUrl)) {
          setPlatform("apple");
        }
      }

      // current 초기화
      setInput((prev) => {
        return {
          ...prev,
          content: [
            ...prev.content,
            {
              title: prev.currentTitle,
              vocal: prev.currentVocal,
              url: prev.currentUrl,
            },
          ],
          currentTitle: "",
          currentVocal: "",
          currentUrl: "",
        };
      });
    },
    [input.currentUrl, input.platform, isAddButtonOn]
  );

  const setImageURL = (url: string) => {
    setInput((prev) => ({ ...prev, url }));
  };

  const setPlatform = (platform: IsPlatform) => {
    setInput((prev) => ({ ...prev, platform }));
  };

  const handleIsVisible = () => {
    if (!input.isVisible && input.content.length < 4) {
      setErrorMsg("4개 이상의 노래를 등록한 후 공개할 수 있습니다");
      return;
    }
    setInput((prev) => ({ ...prev, isVisible: !input.isVisible }));
  };

  const delContent = async (e: React.MouseEvent<HTMLButtonElement>) => {
    const { id } = e.currentTarget;
    await setInput((prev) => {
      if (input.content.length === 1) {
        setInput((prev) => ({ ...prev, platform: "" }));
      }
      return {
        ...prev,
        content: prev.content.filter((element) => element.url !== id),
      };
    });
  };
  const nav = useNavigate();

  useEffect(() => {
    if (input.url && isUpload) {
      const vocalList = Array.from(
        new Set(input.content.map(({ vocal, ...rest }) => vocal))
      );
      addDocument(
        {
          id: input.id,
          uid: user.uid,
          username: user.username,
          platform: input.platform,
          title: input.title,
          url: input.url,
          content: input.content,
          vocalList: vocalList,
          // 수정 예정
          isVisible: input.isVisible,
        },
        user.uid,
        user.username
      );

      // userAlbumNumAdd();
      if (currentDoc && currentDoc.url !== input.url) {
        // 개선
        deleteImage(currentDoc.url);
      }

      nav("/album");
    }
  }, [input.url, isUpload]);

  const onSubmit = async () => {
    if (!isEnterButtonOn) return;

    await upload(input.image, setImageURL);
    setIsUpload(true);
  };

  return (
    <WriteFormBlock isVisible={input.isVisible}>
      <div className="EnterBtnWrapper">
        <button className="VisivleBtn" onClick={handleIsVisible}>
          {input.isVisible ? "> public" : "> private"}
        </button>
        <StateButton onClick={onSubmit} isActivated={isEnterButtonOn}>
          Confirm
        </StateButton>
      </div>
      <div className="TitleInputWrapper">
        <Input
          name="title"
          placeholder="Title"
          value={input.title || ""}
          onChange={onChangeInput}
        />
        <ImgageUploader
          defaultImageUrl={input.url}
          setImageURL={setImageURL}
          setImageFile={setImageFile}
        />
        <Input
          name="platform"
          placeholder="Platform"
          value={input.platform || ""}
          disabled={true}
        />
      </div>
      <form className="ContentInputForm">
        <div className="LeftLine"></div>

        <div className="TitleInput">
          <Input
            name="currentTitle"
            placeholder="Title"
            value={input.currentTitle}
            onChange={onChangeInput}
          />
        </div>
        <div className="VocalInput">
          <InputWithModal
            name="currentVocal"
            value={input.currentVocal}
            onChange={onChangeInput}
            onSelectValue={onSelectVocal}
            vocalList={vocalList}
          />
        </div>
        <div className="UrlInput">
          <Input
            name="currentUrl"
            placeholder="Url"
            value={input.currentUrl}
            onChange={onChangeInput}
          />
          <p className="ErrorMsg">{errorMsg}</p>
        </div>
        <div className="AddBtnWrapper">
          <StateButton onClick={addContent} isActivated={isAddButtonOn}>
            ADD
          </StateButton>
        </div>
      </form>

      <div className="ContentListWrapper">
        {input.content
          .slice(0)
          .reverse()
          .map((element, i) => {
            return (
              <div className="ContentWrapper" key={i}>
                <li>
                  <a href={element.url} target="_blank" rel="noreferrer">
                    {element.title} - {element.vocal}
                  </a>
                  <button id={element.url} onClick={delContent}>
                    ✕
                  </button>
                </li>
              </div>
            );
          })}
      </div>
    </WriteFormBlock>
  );
}
const WriteFormBlock = styled.div<{
  isVisible: boolean;
}>`
  padding: 0px 24px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  height: 100%;
  .EnterBtnWrapper {
    display: flex;
    justify-content: space-between;
    height: 32px;
    width: 100%;
    padding-left: 12px;
    .VisivleBtn {
      width: fit-content;
      background-color: white;
      color: ${({ isVisible }) => isVisible && "#314af3"};
      font-size: 14px;
      border: none;
      &:hover {
        text-decoration: underline;
      }
    }
    // Confirm Button
  }
  .TitleInputWrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .ContentInputForm {
    position: relative;
    height: fit-content;
    width: 100%;
    display: flex;
    justify-content: end;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 2px;
    .LeftLine {
      position: absolute;
      top: 0px;
      left: -24px;
      width: 12px;
      height: 110px;
      background-color: #314af3;
      z-index: 1;
    }
    .TitleInput {
      width: calc(50% - 1px);
    }
    .VocalInput {
      width: calc(50% - 1px);
    }
    .UrlInput {
      width: 100%;
      .ErrorMsg {
        height: 14.5px;
        margin-top: 4px;
        font-size: 12px;
        color: #f94646;
        float: right;
      }
    }
    .AddBtnWrapper {
      position: relative;
      display: flex;
      justify-content: end;
      margin-top: 8px;
      // 개선
      width: 100%;
      height: 36px;
      // Add Button
    }
  }

  .ContentListWrapper {
    height: fit-content;
    overflow-y: auto;
    // scroll del
    .ContentWrapper {
      // drag & drop
      width: fit-content;
      font-size: 14px;
      font-family: gmarket-sans-medium;
      padding: 4px 10px 4px 10px;
      border: 1px solid #314af3;
      border-radius: 1px;
      margin: 0px 0px 6px 0px;
      background: white;
      display: flex;
      align-items: center;

      li {
        display: flex;
        height: 25px;
        align-items: center;
        width: fit-content;
        padding: 2px 0px 2px 4px;
        color: #314af3;
        a {
          max-width: 300px;
          margin-right: 8px;
          font-size: 12px;
          font-family: gmarket-sans-medium;
          white-space: nowrap;
          overflow-x: hidden;
          text-overflow: ellipsis;
        }
        button {
          background: white;
          color: #314af3;
          border: none;
          border-radius: 20px;
          padding: 4px;
          font-size: 11px;
          cursor: pointer;
        }
      }
    }
  }
`;
